import Button from '@app/components/ui/button';
import PdfEditorTextField from '@app/components/ui/input/pdf-editor-text-field';
import { useTranslation } from 'next-i18next';
import { useRef, useState } from 'react';
import { useModal } from '../context';
import { toast } from 'react-toastify';
import { ToastId } from '@app/constants/ToastId';
import { useUpdateUserDisplayNameMutation } from '@app/store/organization/api';
import { Tooltip } from '@mui/material';
import { Info } from 'lucide-react';

export default function ChangeUserNameModal({ name, email, onSuccess }: { name: string; email: string; userId: string; onSuccess?: (newName: string) => void }) {
    const { t } = useTranslation(['common', 'document']);
    const { closeModal } = useModal();
    const renameDocumentRef = useRef<HTMLInputElement | null>(null);
    const [updateUserDisplayName, { isLoading }] = useUpdateUserDisplayNameMutation();

    const [firstName, setFirstName] = useState(name.split(' ')[0] ?? '');
    const [lastName, setLastName] = useState(name.split(' ').slice(1).join(' ') ?? '');
    const [firstNameError, setFirstNameError] = useState('');
    const [lastNameError, setLastNameError] = useState('');

    const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿĀ-žḀ-ỿ0-9'-]{1,20}$/;

    const handleCancel = (e: any) => {
        e.preventDefault();
        closeModal();
    };

    const handleSave = async () => {
        const trimmedFirstName = firstName.trim();
        const trimmedLastName = lastName.trim();

        // Validate input
        let valid = true;
        if (!nameRegex.test(trimmedFirstName)) {
            setFirstNameError(t('invalidFirstName', { ns: 'common' }));
            valid = false;
        }

        if (!nameRegex.test(trimmedLastName)) {
            setLastNameError(t('invalidLastName', { ns: 'common' }));
            valid = false;
        }

        if (!valid) return;

        const newFullName = `${trimmedFirstName} ${trimmedLastName}`;
        if (newFullName === name) {
            closeModal();
            return;
        }

        try {
            await updateUserDisplayName({
                email,
                name: newFullName,
                given_name: trimmedFirstName,
                family_name: trimmedLastName
            }).unwrap();
            onSuccess?.(newFullName);
            closeModal();
            toast.success(`${t('toast.displayNameUpdated')}`, { toastId: ToastId.SUCCESS_TOAST });
        } catch (error) {
            console.log('Error updating display name:', error);
            toast.error(`${t('toast.displayNameUpdatedError')}`, { toastId: ToastId.ERROR_TOAST });
        }
    };

    return (
        <div className="flex h-full w-[30vw] flex-col gap-4 rounded-xl bg-white p-3">
            <h1 className="flex items-center justify-between pb-2 text-xl font-bold">
                {t('updateDisplayUserName', { ns: 'common' })}
                <Tooltip title={t('displayNameOverwrittenDisclaimer', { ns: 'common' })}>
                    <Info className="z-20 h-5 w-4 cursor-pointer text-gray-900" />
                </Tooltip>
            </h1>

            <div className="flex gap-2">
                <PdfEditorTextField
                    data-testid="first-name"
                    id="first-name"
                    label={t('firstName')}
                    placeholder={t('enterFirstName', { ns: 'common' })}
                    inputRef={renameDocumentRef}
                    value={firstName}
                    onChange={(e) => {
                        const val = e.target.value;
                        setFirstName(val);
                        if (!nameRegex.test(val.trim())) {
                            setFirstNameError(t('invalidFirstName', { ns: 'common' }));
                        } else {
                            setFirstNameError('');
                        }
                    }}
                    className="!w-full !rounded-md"
                    error={!!firstNameError}
                    helperText={firstNameError}
                />
                <PdfEditorTextField
                    data-testid="last-name"
                    id="last-name"
                    label={t('lastName')}
                    placeholder={t('enterLastName', { ns: 'common' })}
                    value={lastName}
                    onChange={(e) => {
                        const val = e.target.value;
                        setLastName(val);
                        if (!nameRegex.test(val.trim())) {
                            setLastNameError(t('invalidLastName', { ns: 'common' }));
                        } else {
                            setLastNameError('');
                        }
                    }}
                    className="!w-full !rounded-md"
                    error={!!lastNameError}
                    helperText={lastNameError}
                />
            </div>

            <div className="ml-auto flex w-2/3 items-center justify-end gap-2 pt-6">
                <Button data-testid="cancel-button" onClick={handleCancel} className="flex-1 !border-gray-300 !bg-white !text-black-1 hover:!bg-gray-50" variant="outline" disabled={isLoading}>
                    {t('cancel')}
                </Button>
                <Button data-testid="save-button" className="flex-1" variant="solid" isLoading={isLoading} disabled={isLoading || !!firstNameError || !!lastNameError} onClick={handleSave}>
                    {t('save')}
                </Button>
            </div>
        </div>
    );
}
